import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import {
  useInvoiceHubPdf,
  useInvoiceHubXml,
  useMirrorInvoiceHubPdf,
  useMirrorInvoiceHubXml,
} from './Invoice.hooks';
import {
  useInvoiceRpsDocument,
  useInvoiceUploadDocument,
} from '../../invoice/invoice/invoice.hooks';
import {
  usePdfInvoiceNfs,
  usePdfPreviewInvoiceNfs,
  useXmlInvoiceNfs,
  useXmlPreviewInvoiceNfs,
} from '../../nfs/NfseController/NfseController.hooks';

const useInvoiceFunctions = () => {
  const {
    invoiceId: invoiceIdComingFromTheProvider,
    isNfe,
    isNfc,
    isNfs,
  } = useInvoiceFormContext();

  const mirrorInvoiceHubXml = useMirrorInvoiceHubXml();
  const mirrorInvoiceHubPdf = useMirrorInvoiceHubPdf();

  const invoiceHubPdf = useInvoiceHubPdf();
  const invoiceHubXml = useInvoiceHubXml();

  const pdfPreviewInvoiceNfs = usePdfPreviewInvoiceNfs();
  const xmlPreviewInvoiceNfs = useXmlPreviewInvoiceNfs();

  const pdfInvoiceNfs = usePdfInvoiceNfs();
  const xmlInvoiceNfs = useXmlInvoiceNfs();

  const invoiceRpsDocument = useInvoiceRpsDocument();

  const invoiceUploadDocument = useInvoiceUploadDocument();

  const handleInvoicePdf = async (
    invoiceType?: InvoiceType,
    invoiceIdComingFromTheWorkOrder?: number,
  ): Promise<BlobPart> => {
    let response: BlobPart = new Blob();
    if (isNfe || invoiceType === InvoiceType.NFE) {
      response = (await invoiceHubPdf.mutateAsync({
        invoiceId: Number(
          invoiceIdComingFromTheProvider || invoiceIdComingFromTheWorkOrder,
        ),
        type: InvoiceType.NFE,
      })) as BlobPart;
    }

    if (isNfc || invoiceType === InvoiceType.NFC) {
      response = (await invoiceHubPdf.mutateAsync({
        invoiceId: Number(
          invoiceIdComingFromTheProvider || invoiceIdComingFromTheWorkOrder,
        ),
        type: InvoiceType.NFC,
      })) as BlobPart;
    }

    if (isNfs || invoiceType === InvoiceType.NFS) {
      response = (await pdfInvoiceNfs.mutateAsync(
        Number(
          invoiceIdComingFromTheProvider || invoiceIdComingFromTheWorkOrder,
        ),
      )) as BlobPart;
    }

    return response;
  };

  const handleInvoiceXml = async (
    invoiceType?: InvoiceType,
    invoiceIdComingFromTheWorkOrder?: number,
  ): Promise<BlobPart> => {
    let response: BlobPart = new Blob();
    if (isNfe || invoiceType === InvoiceType.NFE) {
      response = (await invoiceHubXml.mutateAsync({
        invoiceId: Number(
          invoiceIdComingFromTheProvider || invoiceIdComingFromTheWorkOrder,
        ),
        type: InvoiceType.NFE,
      })) as BlobPart;
    }

    if (isNfc || invoiceType === InvoiceType.NFC) {
      response = (await invoiceHubXml.mutateAsync({
        invoiceId: Number(
          invoiceIdComingFromTheProvider || invoiceIdComingFromTheWorkOrder,
        ),
        type: InvoiceType.NFC,
      })) as BlobPart;
    }

    if (isNfs || invoiceType === InvoiceType.NFS) {
      response = (await xmlInvoiceNfs.mutateAsync(
        Number(
          invoiceIdComingFromTheProvider || invoiceIdComingFromTheWorkOrder,
        ),
      )) as BlobPart;
    }

    return response;
  };

  const handleMirrorInvoicePdf = async (
    isBhSpCity: boolean,
  ): Promise<BlobPart> => {
    let response: BlobPart = new Blob();
    if (isNfe) {
      response = (await mirrorInvoiceHubPdf.mutateAsync({
        invoiceId: Number(invoiceIdComingFromTheProvider),
        type: InvoiceType.NFE,
      })) as BlobPart;
    }

    if (isNfc) {
      response = (await mirrorInvoiceHubPdf.mutateAsync({
        invoiceId: Number(invoiceIdComingFromTheProvider),
        type: InvoiceType.NFC,
      })) as BlobPart;
    }

    if (isNfs && isBhSpCity) {
      response = (await pdfPreviewInvoiceNfs.mutateAsync(
        Number(invoiceIdComingFromTheProvider),
      )) as BlobPart;
    }

    return response;
  };

  const handleMirrorInvoiceXml = async (
    isBhSpCity: boolean,
  ): Promise<BlobPart> => {
    let response: BlobPart = new Blob();
    if (isNfe) {
      response = (await mirrorInvoiceHubXml.mutateAsync({
        invoiceId: Number(invoiceIdComingFromTheProvider),
        type: InvoiceType.NFE,
      })) as BlobPart;
    }

    if (isNfc) {
      response = (await mirrorInvoiceHubXml.mutateAsync({
        invoiceId: Number(invoiceIdComingFromTheProvider),
        type: InvoiceType.NFC,
      })) as BlobPart;
    }

    if (isNfs && isBhSpCity) {
      response = (await xmlPreviewInvoiceNfs.mutateAsync(
        Number(invoiceIdComingFromTheProvider),
      )) as BlobPart;
    }

    return response;
  };

  const handleInvoiceRpsPdf = async (): Promise<BlobPart> => {
    const response = (await invoiceRpsDocument.mutateAsync(
      Number(invoiceIdComingFromTheProvider),
    )) as BlobPart;
    return response;
  };

  const handleInvoiceUploadDocument = async (): Promise<BlobPart> => {
    const response = (await invoiceUploadDocument.mutateAsync(
      Number(invoiceIdComingFromTheProvider),
    )) as BlobPart;
    return response;
  };

  const allIsLoadingInternal =
    mirrorInvoiceHubXml.isLoading ||
    mirrorInvoiceHubPdf.isLoading ||
    invoiceHubPdf.isLoading ||
    invoiceHubXml.isLoading ||
    pdfInvoiceNfs.isLoading ||
    xmlInvoiceNfs.isLoading ||
    invoiceRpsDocument.isLoading ||
    invoiceUploadDocument.isLoading;

  return {
    handleInvoicePdf,
    handleInvoiceXml,
    handleMirrorInvoicePdf,
    handleMirrorInvoiceXml,
    handleInvoiceRpsPdf,
    handleInvoiceUploadDocument,
    allIsLoadingInternal,
  };
};

export default useInvoiceFunctions;
